import type { SortOption } from 'src/components/sort-dropdown';

/** Query key for the selected plan-box activity (kebab-case, like `inspector-ids`). */
export const SCHEDULE_URL_ACTIVITY_ID = 'activity-id';

/** Query key for explicit inspector list sort when a plan-box activity is selected. */
export const SCHEDULE_URL_SORT = 'sort';

/** Query key set when entering Schedule from the activity list's manual-plan action. */
export const SCHEDULE_URL_MANUAL_PLAN = 'manual-plan';

/**
 * Unique token per Plan Manually navigation so schedule-view can reload the active
 * plan box even when date/activity-id query params match a prior entry.
 */
export const SCHEDULE_URL_MANUAL_PLAN_ENTRY = 'manual-plan-entry';

/** Query key for the plan-box activity that pins distance inspector sort. */
export const SCHEDULE_URL_SORT_ACTIVITY_ID = 'sort-activity-id';

/** Default inspector sort when a plan-box activity is selected (coverage desc, then distance asc). */
export const SCHEDULE_DEFAULT_PLANBOX_SORT = 'competency_coverage_desc';

const SCHEDULE_SORT_SPECS: Record<string, Pick<SortOption, 'value' | 'api_value' | 'order'>> = {
  name_asc: { value: 'name_asc', api_value: ['name'], order: 'ASC' },
  name_desc: { value: 'name_desc', api_value: ['name'], order: 'DESC' },
  competency_coverage_desc: {
    value: 'competency_coverage_desc',
    api_value: ['competencyCoverage'],
    order: 'DESC',
  },
  distance_asc: { value: 'distance_asc', api_value: ['distance'], order: 'ASC' },
};

export function parseActivityIdFromUrl(
  searchParams: Pick<URLSearchParams, 'get'>
): number | null {
  const raw = searchParams.get(SCHEDULE_URL_ACTIVITY_ID);
  if (!raw?.trim()) return null;
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : null;
}

export function isManualPlanEntryFromUrl(
  searchParams: Pick<URLSearchParams, 'get'>
): boolean {
  const raw = searchParams.get(SCHEDULE_URL_MANUAL_PLAN)?.trim().toLowerCase();
  return raw === '1' || raw === 'true';
}

export function parseSortActivityIdFromUrl(
  searchParams: Pick<URLSearchParams, 'get'>
): number | null {
  const raw = searchParams.get(SCHEDULE_URL_SORT_ACTIVITY_ID);
  if (!raw?.trim()) return null;
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : null;
}

/** Activity id used for server-side distance sort; falls back to activity-id when absent. */
export function resolveInspectorSortActivityIdFromUrl(
  searchParams: Pick<URLSearchParams, 'get'>
): number | null {
  const sort = resolveSortOptionFromUrl(searchParams.get(SCHEDULE_URL_SORT));
  if (sort == null || !isPlanboxDistanceSortOption(sort)) {
    return null;
  }
  return parseSortActivityIdFromUrl(searchParams) ?? parseActivityIdFromUrl(searchParams);
}

export function isValidScheduleSortParam(value: string | null | undefined): boolean {
  if (!value?.trim()) return false;
  return value in SCHEDULE_SORT_SPECS;
}

/** Restores a sort option from the URL; label is unused by the sort menu (options come from CalendarHeader). */
export function resolveSortOptionFromUrl(
  sortParam: string | null | undefined
): SortOption | null {
  if (!sortParam?.trim()) return null;
  const normalized =
    sortParam.trim() === 'distance_desc' ? 'competency_coverage_desc' : sortParam.trim();
  const spec = SCHEDULE_SORT_SPECS[normalized];
  if (!spec) return null;
  return { label: '', ...spec };
}

export function scheduleSortParamFromOption(option: SortOption | null | undefined): string | null {
  if (!option?.value || !isValidScheduleSortParam(option.value)) return null;
  return option.value;
}

export function defaultPlanboxSortOption(): SortOption | null {
  return resolveSortOptionFromUrl(SCHEDULE_DEFAULT_PLANBOX_SORT);
}

/** True when inspector filters changed and at least one competency filter is active. */
export function shouldDefaultToCompetencyCoverageSort(
  filtersChanged: boolean,
  activeCompetencyFilterCount: number
): boolean {
  return filtersChanged && activeCompetencyFilterCount > 0;
}

export function isPlanboxDistanceSortOption(
  option: SortOption | null | undefined
): boolean {
  if (!option?.api_value?.length) return false;
  return option.api_value.some((field) => field === 'distance');
}

export function isPlanboxCompetencyCoverageSortOption(
  option: SortOption | null | undefined
): boolean {
  if (!option?.api_value?.length) return false;
  return option.api_value.some((field) => field === 'competencyCoverage');
}

/** Sort options tied to plan-box scheduling (distance needs a selected activity; competency uses active filters). */
export function isPlanboxActivitySortOption(option: SortOption | null | undefined): boolean {
  return (
    isPlanboxDistanceSortOption(option) || isPlanboxCompetencyCoverageSortOption(option)
  );
}

/**
 * activityId for GET /inspectors.
 * Distance-only sort uses the pinned sort activity.
 * Coverage sort uses the currently selected plan-box activity so the API can nest
 * site distance after plan-box competency coverage.
 */
export function resolveInspectorActivityIdForApi(
  inspectorSortActivityId: number | null | undefined,
  sortOption: SortOption | null | undefined,
  selectedPlanboxActivityId?: number | null
): number | undefined {
  if (isPlanboxDistanceSortOption(sortOption)) {
    return inspectorSortActivityId ?? undefined;
  }
  if (
    isPlanboxCompetencyCoverageSortOption(sortOption) &&
    selectedPlanboxActivityId != null
  ) {
    return selectedPlanboxActivityId;
  }
  return undefined;
}
